// Thailand VAT arithmetic: the dated rate, and the exact-splitting rules for
// VAT-INCLUSIVE amounts.
//
// Marketplace revenue is VAT-inclusive — the buyer paid one number and the
// VAT is inside it (plan §4.3). Everything here rounds in INTEGER SATANG and
// back-solves bases from rounded VAT so that re-summing always reproduces the
// gross exactly. Rounding base and VAT independently drifts by satang and the
// invoice stops tying to what the buyer actually paid.

export interface VatRatePeriod {
    // ISO date the rate applies from, inclusive.
    effectiveFrom: string;
    rate: number;
}

// The statutory rate is 10%, reduced to 7% by royal decree and re-extended
// year by year (plan §4.3). A future change is ONE new entry here — dated, so
// historical documents keep the rate they were issued under and regenerating
// an old period cannot silently re-rate it.
export const VAT_RATES: VatRatePeriod[] = [
    // Backstop covering every date this system will ever see; 7% has held
    // (with a brief 2540s exception that predates all data here) since 1992.
    { effectiveFrom: '1992-01-01', rate: 0.07 },
];

// The rate in force on a given ISO date ('YYYY-MM-DD'), compared as strings —
// correct for ISO dates and keeps timezones out of a question answered in
// Thai calendar days. No date means today's ambient date is NOT assumed;
// callers pass the document's tax-point date. The parameter is optional only
// so pre-dated-rate call sites keep working; they get the latest rate.
export const vatRateAt = (atIsoDate?: string): number => {
    let rate = VAT_RATES[0].rate;
    for (const period of VAT_RATES) {
        if (atIsoDate !== undefined && atIsoDate < period.effectiveFrom) {
            break;
        }
        rate = period.rate;
    }
    return rate;
};

// Splits a VAT-inclusive gross into { base, output } at satang precision.
// Rounds the VAT and back-solves the base, so base + output === gross always.
export const splitVatInclusive = (
    gross: number,
    atIsoDate?: string,
): { base: number; output: number } => {
    const rate = vatRateAt(atIsoDate);
    const output = Math.round(gross * (rate / (1 + rate)) * 100) / 100;
    return { base: Math.round((gross - output) * 100) / 100, output };
};

// Allocates a VAT-inclusive invoice across its lines exactly (spec W5).
//
// Line amounts are money and are snapped to satang first. The invoice-level
// VAT is computed once from the summed gross (identical to splitVatInclusive
// on the total), each line's VAT is rounded proportionally, and the rounding
// residual — at most a few satang — is carried on the LAST line. That is the
// documented convention; do not redistribute it. Invariants, all in integer
// satang, all guaranteed:
//   Σ line.base + Σ line.vat === Σ line gross   (ties to what the buyer paid)
//   Σ line.vat === totalVat                     (report sums match the doc)
//   line.base + line.vat === line gross          (every line self-consistent)
export const allocateVatInclusive = (
    lineGrossAmounts: number[],
    atIsoDate?: string,
): {
    lines: { base: number; vat: number }[];
    totalBase: number;
    totalVat: number;
} => {
    const rate = vatRateAt(atIsoDate);
    const grossSatang = lineGrossAmounts.map((amount) =>
        Math.round(amount * 100),
    );
    const totalGrossSatang = grossSatang.reduce((sum, s) => sum + s, 0);
    // Through splitVatInclusive, not re-derived — the invoice's VAT line and
    // any doc-level split of the same gross must agree to the satang.
    const totalVatSatang = Math.round(
        splitVatInclusive(totalGrossSatang / 100, atIsoDate).output * 100,
    );

    const vatSatang = grossSatang.map((s) =>
        Math.round(s * (rate / (1 + rate))),
    );
    const allocated = vatSatang.reduce((sum, s) => sum + s, 0);
    if (vatSatang.length > 0) {
        vatSatang[vatSatang.length - 1] += totalVatSatang - allocated;
    }

    const lines = grossSatang.map((s, i) => ({
        base: (s - vatSatang[i]) / 100,
        vat: vatSatang[i] / 100,
    }));
    return {
        lines,
        totalBase: (totalGrossSatang - totalVatSatang) / 100,
        totalVat: totalVatSatang / 100,
    };
};
